import React from "react";
import { useGetOrderedLiveboardCategories } from "./hooks/useOrderedLiveboards";
import { LiveboardCategory, OrderedLiveboardsCategory } from "./types";
import { useLiveboardSelection } from "./hooks/useLiveboardSelection";

type LiveboardsOptions = {
  searchTerm: string;
  favoritesOnly: boolean;
};

export type LiveboardsContext = {
  selectedLiveboardId: string | null;
  isLoading: boolean;
  liveboardCategories: LiveboardCategory[];
  changeLiveboardCategoriesOrder: (o: OrderedLiveboardsCategory[]) => void;
  setOptions: React.Dispatch<React.SetStateAction<LiveboardsOptions>>;
};

const liveboardsContext = React.createContext<LiveboardsContext>({
  selectedLiveboardId: null,
  isLoading: true,
  liveboardCategories: [],
  changeLiveboardCategoriesOrder: () => {},
  setOptions: () => {},
});

// eslint-disable-next-line react-refresh/only-export-components
export function useLiveboardsContext() {
  return React.useContext(liveboardsContext);
}

export function LiveboardsProvider({ children }: { children: React.ReactNode }) {
  const [options, setOptions] = React.useState<LiveboardsOptions>({
    searchTerm: "",
    favoritesOnly: false,
  });

  const { liveboardCategories, query, mutation } = useGetOrderedLiveboardCategories(options);

  const mutateFn = mutation.mutate;
  const changeLiveboardCategoriesOrder = React.useCallback(
    (o: OrderedLiveboardsCategory[]) => {
      mutateFn({ newLiveboardCategoriesOrder: o });
    },
    [mutateFn],
  );

  const { selectedLiveboardId } = useLiveboardSelection(liveboardCategories);

  return (
    <liveboardsContext.Provider
      value={{
        selectedLiveboardId,
        liveboardCategories,
        changeLiveboardCategoriesOrder,
        setOptions,
        isLoading: query.isLoading,
      }}
    >
      {children}
    </liveboardsContext.Provider>
  );
}
